/* LUVIRE Calculator Core v1.0.0
 * Pure browser-side numerology calculations. No network requests, cookies,
 * persistent browser storage, or raw-value analytics are used by this runtime.
 */
(function () {
  "use strict";

  const VERSION = "1.0.0";
  const MASTERS = new Set([11, 22, 33]);
  const VOWELS = new Set(["A", "E", "I", "O", "U"]);
  const CHALDEAN = {
    A: 1, I: 1, J: 1, Q: 1, Y: 1,
    B: 2, K: 2, R: 2,
    C: 3, G: 3, L: 3, S: 3,
    D: 4, M: 4, T: 4,
    E: 5, H: 5, N: 5, X: 5,
    U: 6, V: 6, W: 6,
    O: 7, Z: 7,
    F: 8, P: 8
  };
  const LATIN_EXPANSIONS = {
    "Æ": "AE", "æ": "ae",
    "Œ": "OE", "œ": "oe",
    "Ø": "O", "ø": "o",
    "Ł": "L", "ł": "l",
    "Ð": "D", "ð": "d",
    "Þ": "TH", "þ": "th",
    "Đ": "D", "đ": "d",
    "ß": "SS", "ẞ": "SS"
  };
  const NUMBER_MEANINGS = {
    0: {
      title: "No numerical gap",
      keywords: ["choice", "breadth", "potential"],
      summary: "Zero means there is no numerical gap in this part of the calculation; its exact meaning depends on the calculator.",
      prompt: "What matters enough to become your chosen center?",
      overview: "Zero is read in context. In a Bridge it means no gap; in a Challenge it means no single numerical tension dominates.",
      potential: "Flexibility, openness, and the ability to choose a response suited to the situation.",
      friction: "Openness can become drift or delay when no priority is chosen.",
      practice: "Name the decision, choose one guiding value, and take the smallest reversible step."
    },
    1: {
      title: "The Initiator",
      keywords: ["independence", "direction", "courage"],
      summary: "One is linked with initiative, independence, and the courage to make the first move.",
      prompt: "Where can you lead without closing yourself to support?",
      overview: "One is about taking initiative: choosing a direction, making the first move, and learning how to stand on your own without shutting other people out.",
      potential: "Initiative, directness, originality, and the courage to begin before every variable is known.",
      friction: "Self-direction may harden into impatience, defensiveness, or refusal of useful support.",
      practice: "Own one clear action and invite one perspective that could improve it."
    },
    2: {
      title: "The Harmonizer",
      keywords: ["sensitivity", "cooperation", "timing"],
      summary: "Two is the number of cooperation: noticing nuance, making room, and finding a workable rhythm with other people.",
      prompt: "What would cooperation look like without self-erasure?",
      overview: "Two is about cooperation, sensitivity, and the space between two points of view. Real harmony still leaves room for your own preference.",
      potential: "Careful listening, diplomacy, patient timing, and awareness of nuances that faster approaches miss.",
      friction: "Sensitivity may become hesitation, indirectness, or accommodation chosen mainly to preserve approval.",
      practice: "State your preference before agreeing, then make one request that leaves room for both people."
    },
    3: {
      title: "The Expression",
      keywords: ["creativity", "voice", "joy"],
      summary: "Three is associated with voice, imagination, and giving an idea a form other people can enjoy or understand.",
      prompt: "What wants to be expressed with greater honesty?",
      overview: "Three is about expression: finding the words, image, joke, or story that lets an idea reach another person.",
      potential: "Storytelling, humor, creative synthesis, warmth, and making difficult material easier to approach.",
      friction: "Energy may scatter across beginnings, while charm or performance can replace honest contact.",
      practice: "Finish a small version of one idea; make the meaning true before making it elegant."
    },
    4: {
      title: "The Builder",
      keywords: ["structure", "craft", "reliability"],
      summary: "Four is associated with structure, steady effort, and work that lasts.",
      prompt: "Which structure supports life, and which one has become a cage?",
      overview: "In a reading, Four points to the part of life that asks you to build: skills, routines, security, or something other people can rely on.",
      potential: "Patience, sound judgment, and the follow-through to carry an idea past the exciting beginning.",
      friction: "Useful structure can harden into overwork, rigid standards, or loyalty to a plan that no longer helps.",
      practice: "Keep one routine that genuinely supports you this week. Drop one rule you follow only from habit."
    },
    5: {
      title: "The Explorer",
      keywords: ["freedom", "change", "experience"],
      summary: "Five brings movement, variety, and learning by trying things in the real world.",
      prompt: "What kind of freedom also protects what you value?",
      overview: "Five is about movement, variety, and the kind of freedom that comes from trying something in the real world.",
      potential: "Adaptability, curiosity, persuasive communication, experimentation, and timely course correction.",
      friction: "Novelty may become restlessness, impulsive commitments, or escape from the work that makes freedom durable.",
      practice: "Define the freedom and its responsibility, then run one bounded experiment with a review date."
    },
    6: {
      title: "The Steward",
      keywords: ["care", "responsibility", "beauty"],
      summary: "Six is associated with care, responsibility, and the urge to make home, work, or relationships feel more whole.",
      prompt: "How can care include your own needs and clear boundaries?",
      overview: "Six is about care, responsibility, belonging, and the wish to make a home, team, or relationship feel more whole.",
      potential: "Loyalty, thoughtful service, mediation, aesthetic judgment, and making an environment more humane.",
      friction: "Care may become control, perfectionism, unsolicited fixing, or resentment when boundaries blur.",
      practice: "Ask what help was actually requested, what is truly yours to carry, and where a clear boundary would make care easier."
    },
    7: {
      title: "The Seeker",
      keywords: ["insight", "discernment", "depth"],
      summary: "Seven turns inward to question, research, and look beneath easy answers.",
      prompt: "What can be tested in experience instead of only analyzed?",
      overview: "Seven is about inquiry, privacy, and looking beneath the easy answer. Thinking helps most when it eventually meets real evidence.",
      potential: "Research, pattern recognition, intellectual independence, concentration, and patience with difficult questions.",
      friction: "Careful thinking can turn into isolation, suspicion, or preparation that never becomes action.",
      practice: "Name what evidence could change your view and test one part of the idea in reality."
    },
    8: {
      title: "The Executive",
      keywords: ["power", "exchange", "mastery"],
      summary: "Eight deals with power, resources, and the consequences of how they are used.",
      prompt: "How can ambition serve something larger than recognition?",
      overview: "Eight asks how you handle power, money, time, responsibility, and the consequences of a decision. It does not predict wealth.",
      potential: "Strategy, endurance, negotiation, and responsible stewardship of money, time, or authority.",
      friction: "Effectiveness may narrow into control, work without recovery, or measuring personal worth only through outcomes.",
      practice: "Define success with one practical measure and one measure of how people are affected. Then ask who carries the cost."
    },
    9: {
      title: "The Humanitarian",
      keywords: ["completion", "compassion", "perspective"],
      summary: "Nine is linked with completion, perspective, and knowing when to contribute—and when to let go.",
      prompt: "What can be released without denying what it taught you?",
      overview: "Nine is about completion, perspective, and deciding what to contribute, what to forgive, and what to let end.",
      potential: "Generosity, synthesis, artistic or social perspective, and service informed by experience.",
      friction: "Wide concern may become diffuse obligation, disillusionment, martyrdom, or difficulty allowing a chapter to end.",
      practice: "Name what the chapter gave and cost, then choose one contribution and one thing to release."
    },
    11: {
      title: "The Illuminator",
      keywords: ["intuition", "inspiration", "sensitivity"],
      summary: "Eleven is traditionally read as 11/2: the sensitivity of Two with more intensity, inspiration, and nervous energy.",
      prompt: "What simple practice can give a strong insight a stable form?",
      overview: "Eleven is a master number with a Two base. It symbolizes heightened inspiration or sensitivity, not proof of intuition or spiritual status.",
      potential: "Vision, subtle perception, and translating strong ideas into language or forms other people can use.",
      friction: "Sensitivity may amplify anxiety, projection, exceptionalism, or certainty formed before an insight is tested.",
      practice: "Separate observation from interpretation and give the insight one ordinary, testable form."
    },
    22: {
      title: "The Master Builder",
      keywords: ["vision", "systems", "service"],
      summary: "Twenty-two is traditionally read as 22/4: practical structure stretched toward a larger, longer-term purpose.",
      prompt: "What is the smallest durable foundation for the larger vision?",
      overview: "Twenty-two is a master number with a Four base. It symbolizes giving a broad vision durable form, not guaranteed scale or achievement.",
      potential: "Systems thinking, patient execution, coordination, and building useful structures beyond immediate recognition.",
      friction: "Vision may create overwhelm, harsh standards, or scale without a maintainable and relevant foundation.",
      practice: "Build the smallest version that delivers value and proof before adding complexity."
    },
    33: {
      title: "The Compassionate Teacher",
      keywords: ["service", "restorative care", "responsibility"],
      summary: "Thirty-three is traditionally read as 33/6: care and responsibility expressed through teaching, guidance, or service.",
      prompt: "How can service strengthen others rather than make them dependent?",
      overview: "Thirty-three is a master number with a Six base. It symbolizes compassionate teaching, not moral authority or duty to carry others.",
      potential: "Patient guidance, creative service, and care that strengthens another person's choice and confidence.",
      friction: "Idealized service may become exhaustion, guilt, blurred boundaries, or pressure to remain indispensable.",
      practice: "Give service a clear scope, consent, and endpoint; ask whether it helps everyone keep their own voice and choices."
    }
  };

  function digits(value) {
    return String(value == null ? "" : value).replace(/\D/g, "");
  }

  function sumDigits(value) {
    return digits(value).split("").reduce((total, digit) => total + Number(digit), 0);
  }

  function reduceNumber(value, preserveMasters) {
    const preserve = preserveMasters !== false;
    let current = Math.abs(Number(value));
    const chain = [current];
    while (current > 9 && !(preserve && MASTERS.has(current))) {
      current = sumDigits(current);
      chain.push(current);
    }
    return { value: current, chain: chain };
  }

  function baseValue(value) {
    if (value === 11) return 2;
    if (value === 22) return 4;
    if (value === 33) return 6;
    return value;
  }

  function normalizeName(value) {
    const expanded = String(value || "")
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .replace(/[ÆæŒœØøŁłÐðÞþĐđßẞ]/g, function (letter) { return LATIN_EXPANSIONS[letter] || letter; })
      .replace(/[’‘ʻʼ]/g, "'")
      .replace(/[‐‑‒–—―−]/g, "-")
      .replace(/\s+/gu, " ");
    const unsupportedLetter = Array.from(expanded).some(function (character) {
      return character.codePointAt(0) > 127 && /\p{Letter}/u.test(character);
    });
    if (unsupportedLetter) {
      throw new Error("Use a consistent Latin-letter transliteration for this name.");
    }
    return expanded
      .toUpperCase()
      .replace(/[^A-Z' -]/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }

  function letters(value) {
    return normalizeName(value).replace(/[^A-Z]/g, "").split("");
  }

  function pythagorean(letter) {
    const code = String(letter).charCodeAt(0) - 65;
    return code >= 0 && code < 26 ? (code % 9) + 1 : 0;
  }

  function letterValues(value, system) {
    return letters(value).map(function (letter) {
      return {
        letter: letter,
        value: system === "chaldean" ? (CHALDEAN[letter] || 0) : pythagorean(letter)
      };
    });
  }

  function nameParts(value, label) {
    const normalized = requireName(value, label);
    return normalized
      .split(/[\s-]+/)
      .map(function (part) { return part.replace(/[^A-Z]/g, ""); })
      .filter(Boolean);
  }

  function initialData(value, label) {
    const parts = nameParts(value, label);
    const mapped = parts.map(function (part) {
      return { letter: part.charAt(0), value: pythagorean(part.charAt(0)) };
    });
    const raw = mapped.reduce(function (total, item) { return total + item.value; }, 0);
    const reduced = reduceNumber(raw, false);
    return { parts: parts, mapped: mapped, raw: raw, value: reduced.value, chain: reduced.chain };
  }

  function requireName(value, label) {
    const normalized = normalizeName(value);
    if (letters(normalized).length < 2) {
      throw new Error("Enter at least two Latin letters for " + (label || "the name") + ".");
    }
    return normalized;
  }

  function parseDate(value, label) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
    if (!match) throw new Error("Enter a valid " + (label || "date") + ".");
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
    const monthLengths = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (year < 1 || month < 1 || month > 12 || day < 1 || day > monthLengths[month - 1]) {
      throw new Error("Enter a real calendar " + (label || "date") + ".");
    }
    const parsed = { year: year, month: month, day: day, iso: match[0] };
    if (/\bbirth\b/i.test(String(label || ""))) {
      const now = new Date();
      const today = [
        String(now.getFullYear()).padStart(4, "0"),
        String(now.getMonth() + 1).padStart(2, "0"),
        String(now.getDate()).padStart(2, "0")
      ].join("-");
      if (parsed.iso > today) {
        throw new Error("Date of birth cannot be in the future.");
      }
    }
    return parsed;
  }

  function compareDates(first, second) {
    return first.iso.localeCompare(second.iso);
  }

  function completedAge(birth, target) {
    let age = target.year - birth.year;
    if (
      target.month < birth.month ||
      (target.month === birth.month && target.day < birth.day)
    ) {
      age -= 1;
    }
    return age;
  }

  function targetYear(value) {
    const year = Number(value);
    if (!Number.isInteger(year) || year < 1000 || year > 9999) {
      throw new Error("Enter a four-digit year between 1000 and 9999.");
    }
    return year;
  }

  function dateRaw(date) {
    return sumDigits(
      String(date.year) +
      String(date.month).padStart(2, "0") +
      String(date.day).padStart(2, "0")
    );
  }

  function dateDigitFormula(date, label) {
    const values = date.iso.replace(/-/g, "").split("");
    return (label || "Date digits") + ": " + values.join(" + ") + " = " + dateRaw(date);
  }

  function lifePath(date) {
    const raw = dateRaw(date);
    const reduced = reduceNumber(raw, true);
    return { value: reduced.value, raw: raw, chain: reduced.chain };
  }

  function birthday(date) {
    const reduced = reduceNumber(date.day, true);
    return { value: reduced.value, raw: date.day, chain: reduced.chain };
  }

  function attitude(date) {
    const raw = date.month + date.day;
    const reduced = reduceNumber(raw, true);
    return { value: reduced.value, raw: raw, chain: reduced.chain };
  }

  function nameNumber(value, mode, system) {
    const normalized = requireName(value);
    let mapped = letterValues(normalized, system || "pythagorean");
    if (mode === "vowels") mapped = mapped.filter(function (item) { return VOWELS.has(item.letter); });
    if (mode === "consonants") mapped = mapped.filter(function (item) { return !VOWELS.has(item.letter); });
    const raw = mapped.reduce(function (total, item) { return total + item.value; }, 0);
    const reduced = reduceNumber(raw, system !== "chaldean");
    return {
      value: reduced.value,
      raw: raw,
      chain: reduced.chain,
      normalized: normalized,
      mapped: mapped
    };
  }

  function personalYear(date, year) {
    const raw = date.month + date.day + sumDigits(year);
    const reduced = reduceNumber(raw, false);
    return { value: reduced.value, raw: raw, chain: reduced.chain };
  }

  function personalMonth(date, target) {
    const py = personalYear(date, target.year);
    const raw = py.value + target.month;
    const reduced = reduceNumber(raw, false);
    return { value: reduced.value, raw: raw, chain: reduced.chain, personalYear: py.value };
  }

  function personalDay(date, target) {
    const pm = personalMonth(date, target);
    const raw = pm.value + target.day;
    const reduced = reduceNumber(raw, false);
    return {
      value: reduced.value,
      raw: raw,
      chain: reduced.chain,
      personalYear: pm.personalYear,
      personalMonth: pm.value
    };
  }

  function numberMeaning(number) {
    return NUMBER_MEANINGS[number] || NUMBER_MEANINGS[reduceNumber(number, false).value] || NUMBER_MEANINGS[9];
  }

  function formula(label, raw, chain) {
    const path = (chain || [raw]).join(" → ");
    return label + ": " + path;
  }

  function metric(label, value, note) {
    return { label: label, value: String(value), note: note || "" };
  }

  function section(title, body) {
    return { title: title, body: body };
  }

  function uniqueReadingValues(values) {
    const seen = new Set();
    return (values || []).map(Number).filter(function (value) {
      if (!Number.isFinite(value)) return false;
      const reduced = numberMeaning(value) ? (NUMBER_MEANINGS[value] ? value : reduceNumber(value, false).value) : value;
      if (!NUMBER_MEANINGS[reduced] || seen.has(reduced)) return false;
      seen.add(reduced);
      return true;
    });
  }

  function readingKind(label) {
    const normalized = String(label || "").toLowerCase();
    if (normalized.includes("relationship") || normalized.includes("compatibility") || normalized.includes("wedding")) {
      return "relationship";
    }
    if (normalized.includes("year") || normalized.includes("month") || normalized.includes("day") || normalized.includes("essence") || normalized.includes("cycle") || normalized.includes("pinnacle") || normalized.includes("challenge")) {
      return "cycle";
    }
    if (normalized.includes("name") || normalized.includes("expression") || normalized.includes("destiny") || normalized.includes("soul urge") || normalized.includes("personality") || normalized.includes("cornerstone") || normalized.includes("capstone") || normalized.includes("vowel") || normalized.includes("passion") || normalized.includes("balance")) {
      return "name";
    }
    if (normalized.includes("house") || normalized.includes("phone") || normalized.includes("vehicle") || normalized.includes("alignment") || normalized.includes("number set") || normalized.includes("pattern") || normalized.includes("mirror") || normalized.includes("lo shu")) {
      return "pattern";
    }
    return "core";
  }

  function readingFrame(label) {
    const kind = readingKind(label);
    if (kind === "relationship") {
      return "These numbers give you a language for difference. The useful part is noticing how each person plans, speaks, responds, and repairs—not trying to turn the relationship into a score.";
    }
    if (kind === "cycle") {
      return "A cycle number describes the tone of a period. Use it to choose what deserves attention; it is not a calendar of events that must happen.";
    }
    if (kind === "name") {
      return "This result belongs to the spelling you entered. A changed spelling may give a different number, so read it as one angle on the name rather than a fixed identity.";
    }
    if (kind === "pattern") {
      return "The number gives you a prompt for reflection. It does not change the odds, cause an event, or replace the practical facts around the choice.";
    }
    return "This is one part of the chart, not a label for the whole person. Keep the parts that match your experience and leave the parts that do not.";
  }

  function joinNumberThemes(values, property) {
    const visible = uniqueReadingValues(values).slice(0, 4);
    if (visible.length === 1) return numberMeaning(visible[0])[property];
    return visible.map(function (value) {
      const meaning = numberMeaning(value);
      return value + " · " + meaning.title + " — " + meaning[property];
    }).join(" ");
  }

  function readingHeadings(label, multiple) {
    const kind = readingKind(label);
    if (kind === "relationship") {
      return {
        overview: multiple ? "What each number brings" : "What this number brings",
        potential: "Where you may click",
        friction: "Where translation helps",
        practice: "A question worth asking"
      };
    }
    if (kind === "cycle") {
      return {
        overview: multiple ? "The rhythm across these cycles" : "The tone of this cycle",
        potential: "A good use of this period",
        friction: "Watch for",
        practice: "A simple plan"
      };
    }
    if (kind === "name") {
      return {
        overview: multiple ? "What these name numbers emphasize" : "What this spelling emphasizes",
        potential: "How it can come through",
        friction: "When it gets too loud",
        practice: "Try this"
      };
    }
    if (kind === "pattern") {
      return {
        overview: multiple ? "What the pattern contains" : "What this number contains",
        potential: "A useful way to read it",
        friction: "What not to assume",
        practice: "Bring it back to real life"
      };
    }
    return {
      overview: multiple ? "How these numbers fit together" : "What your number means here",
      potential: "Where it helps",
      friction: "When it gets too loud",
      practice: "Try this"
    };
  }

  function plainNextStep(label) {
    const kind = readingKind(label);
    if (kind === "relationship") {
      return "Choose one difference the result names and ask the other person how it feels from their side. A direct answer is more useful than guessing from the number.";
    }
    if (kind === "cycle") {
      return "Choose one realistic priority for this period and one habit that supports it. Review what actually happened before drawing a conclusion about the cycle.";
    }
    if (kind === "name") {
      return "Compare the result with how this spelling is really used—in introductions, work, family, or creative life. Keep the description only where it helps.";
    }
    if (kind === "pattern") {
      return "Write down where you noticed the number and what was on your mind. The surrounding moment usually gives the reflection more meaning than repetition alone.";
    }
    return "Choose one sentence that feels useful and turn it into a small action this week. Ignore anything that does not match your experience.";
  }

  function buildReadingSections(label, values, suppliedSections) {
    const readingValues = uniqueReadingValues(values);
    const sections = [];
    const headings = readingHeadings(label, readingValues.length > 1);
    if (readingValues.length === 1) {
      sections.push(section(
        headings.overview,
        readingFrame(label) + " " + joinNumberThemes(readingValues, "overview")
      ));
      sections.push(section(headings.potential, joinNumberThemes(readingValues, "potential")));
      sections.push(section(headings.friction, joinNumberThemes(readingValues, "friction")));
      sections.push(section(
        headings.practice,
        joinNumberThemes(readingValues, "practice") + " Reflection question: " +
          numberMeaning(readingValues[0]).prompt
      ));
    } else if (readingValues.length > 1) {
      sections.push(section(headings.overview, readingFrame(label)));
      readingValues.slice(0, 4).forEach(function (value) {
        const meaning = numberMeaning(value);
        sections.push(section(
          value + " · " + meaning.title,
          meaning.overview + " At its best: " + meaning.potential + " When it is overdone: " + meaning.friction
        ));
      });
      sections.push(section(
        headings.practice,
        readingValues.slice(0, 2).map(function (value) {
          return numberMeaning(value).practice;
        }).join(" ") + " Ask: " + readingValues.slice(0, 2).map(function (value) {
          return numberMeaning(value).prompt;
        }).join(" ")
      ));
    }
    (suppliedSections || []).forEach(function (item) {
      sections.push(item);
    });
    if (sections.length < 4) {
      sections.push(section("Take it into real life", plainNextStep(label)));
    }
    sections.push(section(
      "How we got this result",
      "Open the calculation above to follow every total and reduction. The math follows the method shown; the reading is an interpretation, not a personality test or prediction."
    ));
    return sections;
  }

  function result(primary, label, formulaSteps, metrics, sections, extra) {
    const options = extra || {};
    const numeric = Number(primary);
    const interpretPrimary = options.interpret_primary !== false;
    const requestedValues = Object.prototype.hasOwnProperty.call(options, "reading_values")
      ? options.reading_values
      : (interpretPrimary && Number.isFinite(numeric) ? [numeric] : []);
    const readingValues = uniqueReadingValues(requestedValues);
    const meaning = readingValues.length === 1 ? numberMeaning(readingValues[0]) : null;
    const publicExtra = Object.assign({}, options);
    delete publicExtra.interpret_primary;
    delete publicExtra.reading_values;
    return Object.assign({
      schema_version: "1.0",
      algorithm_version: VERSION,
      interpretation_version: "2.1.0",
      primary: String(primary),
      label: label,
      title: meaning ? meaning.title : label,
      summary: meaning ? meaning.summary : "Your calculation is ready.",
      keywords: meaning ? meaning.keywords : [],
      formula_steps: formulaSteps || [],
      metrics: metrics || [],
      sections: buildReadingSections(label, readingValues, sections || []),
      reading_values: readingValues,
      reading_scope: "free interpretation",
      disclaimer: "The calculation follows the method shown. The interpretation is for reflection, not a personality test or prediction."
    }, publicExtra);
  }

  function singleDateNumber(input, kind) {
    const date = parseDate(input.birth_date, "date of birth");
    const data = kind === "birthday" ? birthday(date) : kind === "attitude" ? attitude(date) : lifePath(date);
    const labels = {
      birthday: "Birthday Number",
      attitude: "Attitude Number",
      life_path: "Life Path"
    };
    const source = kind === "birthday"
      ? "calendar day " + date.day
      : kind === "attitude"
        ? date.month + " + " + date.day
        : dateDigitFormula(date, "Birth-date digits");
    return result(
      data.value,
      labels[kind],
      [source, formula("Reduction", data.raw, data.chain)],
      [metric("Original total", data.raw), metric("Final number", data.value)],
      null,
      { calculation: data }
    );
  }

  function idealPartner(input) {
    const date = parseDate(input.birth_date, "date of birth");
    const readingVersion = String(input.reading_version || "").trim().toLowerCase();
    if (readingVersion !== "woman" && readingVersion !== "man") {
      throw new Error("Choose Woman or Man to continue.");
    }
    const readings = window.LUVIRE_IDEAL_PARTNER_READINGS;
    if (!readings || !readings[readingVersion]) {
      throw new Error("The Ideal Partner reading asset is unavailable.");
    }
    const reduced = reduceNumber(date.day, false);
    const profile = readings[readingVersion][reduced.value];
    if (!profile) throw new Error("The Ideal Partner reading is unavailable for this birth day.");
    const matchingDays = [];
    for (let day = 1; day <= 31; day += 1) {
      if (reduceNumber(day, false).value === reduced.value) matchingDays.push(day);
    }
    const readingLabel = readingVersion === "woman" ? "Woman" : "Man";
    return result(
      reduced.value,
      "Your Relationship Archetype",
      [
        "Calendar day: " + date.day + " (month and year are validated but not used in this method)",
        formula("Birth-day reduction", date.day, reduced.chain),
        "Reading selected: " + readingLabel
      ],
      [
        metric("Calendar day", date.day),
        metric("Birth-day root", reduced.value, matchingDays.join(", ")),
        metric("Reading", readingLabel)
      ],
      [
        section("Your natural style", profile.profile),
        section("Qualities that may complement you", profile.traits.join(" · ")),
        section("Relationship insight", profile.insight),
        section("A question to take with you", profile.prompt)
      ],
      {
        interpret_primary: false,
        reading_values: [],
        title: profile.title,
        summary: profile.profile,
        keywords: ["birth-day " + reduced.value, "relationship needs", "ideal partner"],
        reading_version: readingVersion,
        archetype_key: readingVersion + "_" + reduced.value,
        matching_days: matchingDays,
        ideal_partner_traits: profile.traits,
        calculation: {
          calendar_day: date.day,
          root: reduced.value,
          chain: reduced.chain
        }
      }
    );
  }

  function partnerGallery(input) {
    const date = parseDate(input.birth_date, "date of birth");
    const reduced = reduceNumber(date.day, false);
    const matchingDays = [];
    for (let day = 1; day <= 31; day += 1) {
      if (reduceNumber(day, false).value === reduced.value) matchingDays.push(day);
    }
    return result(
      reduced.value,
      "Your Illustrated Partner Profile",
      [
        "Calendar day: " + date.day + " (month and year are validated but not used in this method)",
        formula("Birth-day reduction", date.day, reduced.chain)
      ],
      [
        metric("Calendar day", date.day),
        metric("Birth-day root", reduced.value, matchingDays.join(", ")),
        metric("Illustrated chapters", 7)
      ],
      [
        section("How the number is chosen", "This method uses the calendar day you were born and reduces it to a root from 1 to 9."),
        section("What the gallery contains", "The seven illustrated chapters form one continuous partner-archetype reading rather than seven separate results."),
        section("How to use the reading", "Notice which descriptions help you name real preferences, boundaries, and patterns you have observed in relationships."),
        section("What it cannot tell you", "A birth number cannot identify a future person, prove compatibility, or predict whether a relationship will succeed."),
        section("Keep real context", "Use the symbolic profile beside lived behavior, communication, consent, safety, and the choices of both people.")
      ],
      {
        interpret_primary: false,
        reading_values: [],
        title: "Birth Number " + reduced.value,
        summary: "Seven illustrated chapters are ready for your birth-number partner archetype.",
        keywords: ["birth-day " + reduced.value, "partner archetype", "illustrated reading"],
        matching_days: matchingDays,
        gallery_chapters: 7,
        calculation: {
          calendar_day: date.day,
          root: reduced.value,
          chain: reduced.chain
        }
      }
    );
  }

  function nameSingle(input, mode, label) {
    const data = nameNumber(input.full_name || input.name_to_test || input.baby_name, mode);
    const selected = mode === "vowels" ? "vowels" : mode === "consonants" ? "consonants" : "all letters";
    const emptyLayer = data.mapped.length === 0;
    const emptyDescription = mode === "vowels"
      ? "This spelling contains no A, E, I, O, or U. In this calculation, Y stays in the consonant group."
      : "This spelling contains no consonants under the A–Z method shown here.";
    return result(
      data.value,
      label,
      [
        "Normalized name: " + data.normalized,
        selected + ": " + data.mapped.map(function (item) { return item.letter + "=" + item.value; }).join(" · "),
        formula("Reduction", data.raw, data.chain)
      ],
      [metric("Compound total", data.raw), metric("Letters counted", data.mapped.length)],
      emptyLayer ? [
        section("No selected letters", emptyDescription),
        section("Method boundary", "Zero here records an empty letter subset; it is not interpreted as a substantive Zero name number."),
        section("Next step", "Review the spelling and the disclosed vowel policy before comparing this result with another calculator.")
      ] : null,
      emptyLayer ? {
        interpret_primary: false,
        reading_values: [],
        title: "No " + selected + " to calculate",
        summary: emptyDescription,
        keywords: [],
        calculation: data
      } : { calculation: data }
    );
  }

  function fullChart(input) {
    const date = parseDate(input.birth_date, "date of birth");
    const name = requireName(input.full_name, "the full birth name");
    const lp = lifePath(date);
    const bd = birthday(date);
    const ex = nameNumber(name, "all");
    const su = nameNumber(name, "vowels");
    const pe = nameNumber(name, "consonants");
    const maturityRaw = lp.value + ex.value;
    const ma = reduceNumber(maturityRaw, true);
    return result(
      lp.value,
      "Core Numerology Chart",
      [
        formula("Life Path", lp.raw, lp.chain),
        formula("Expression", ex.raw, ex.chain),
        formula("Soul Urge", su.raw, su.chain),
        formula("Personality", pe.raw, pe.chain),
        formula("Maturity", maturityRaw, ma.chain)
      ],
      [
        metric("Life Path", lp.value, "full birth date"),
        metric("Birthday", bd.value, "calendar day"),
        metric("Expression", ex.value, "all name letters"),
        metric("Soul Urge", su.value, "vowels"),
        metric("Personality", pe.value, "consonants"),
        metric("Maturity", ma.value, "Life Path + Expression")
      ],
      [
        section("Read the pattern", "Repeated values show emphasis; contrasting values describe different parts of the same person rather than a contradiction."),
        section("Start here", numberMeaning(lp.value).summary + " " + numberMeaning(ex.value).summary),
        section("Reflection prompt", "Where do the Life Path direction and Expression tools already cooperate—and where do they ask for different pacing?")
      ],
      {
        interpret_primary: false,
        reading_values: [lp.value, bd.value, ex.value, su.value, pe.value, ma.value],
        title: "Your core numerology chart",
        summary: "Six core values are shown together so the chart can be read as a layered pattern rather than reducing a person to one number.",
        keywords: ["core chart", "birth date", "name layers"],
        chart: { life_path: lp.value, birthday: bd.value, expression: ex.value, soul_urge: su.value, personality: pe.value, maturity: ma.value }
      }
    );
  }

  function maturity(input) {
    const date = parseDate(input.birth_date, "date of birth");
    const lp = lifePath(date);
    const ex = nameNumber(input.full_name, "all");
    const raw = lp.value + ex.value;
    const reduced = reduceNumber(raw, true);
    return result(
      reduced.value,
      "Maturity Number",
      [
        formula("Life Path", lp.raw, lp.chain),
        formula("Expression", ex.raw, ex.chain),
        lp.value + " + " + ex.value + " = " + raw,
        formula("Maturity reduction", raw, reduced.chain)
      ],
      [metric("Life Path", lp.value), metric("Expression", ex.value), metric("Maturity", reduced.value)]
    );
  }

  function initials(input) {
    const data = initialData(input.full_name);
    return result(
      data.value,
      "Balance Number",
      [
        "Initials: " + data.mapped.map(function (item) { return item.letter + "=" + item.value; }).join(" · "),
        formula("Single-digit reduction", data.raw, data.chain)
      ],
      [metric("Name parts", data.parts.length), metric("Initial total", data.raw)],
      null,
      { calculation: data }
    );
  }

  function rationalThought(input) {
    const date = parseDate(input.birth_date, "date of birth");
    const first = nameNumber(input.first_name, "all");
    const raw = first.raw + date.day;
    const reduced = reduceNumber(raw, false);
    return result(
      reduced.value,
      "Rational Thought Number",
      [
        formula("First-name value", first.raw, first.chain),
        first.raw + " + birth day " + date.day + " = " + raw,
        formula("Single-digit reduction", raw, reduced.chain)
      ],
      [metric("First-name total", first.raw), metric("Birth day", date.day)]
    );
  }

  function frequencyData(value) {
    const normalized = requireName(value);
    const counts = {};
    for (let number = 1; number <= 9; number += 1) counts[number] = 0;
    letterValues(normalized).forEach(function (item) { counts[item.value] += 1; });
    return { normalized: normalized, counts: counts };
  }

  function hiddenPassion(input) {
    const data = frequencyData(input.full_name);
    const maximum = Math.max.apply(null, Object.values(data.counts));
    const winners = Object.keys(data.counts).filter(function (key) { return data.counts[key] === maximum; });
    return result(
      winners.join(" & "),
      "Hidden Passion Pattern",
      ["Letter-value frequencies: " + Object.keys(data.counts).map(function (key) { return key + ":" + data.counts[key]; }).join(" · ")],
      winners.map(function (winner) { return metric("Repeated value", winner, maximum + " occurrences"); }),
      [
        section("What stands out", winners.length > 1 ? "The name contains a genuine tie, so every tied value is retained." : "One value occurs more often than any other."),
        section("Use it well", "Frequency suggests emphasis, not superiority. Read the repeated value beside missing and less frequent values."),
        section("Reflection prompt", "Where does this repeated style appear naturally, and where might it crowd out another response?")
      ],
      {
        interpret_primary: false,
        reading_values: winners.map(Number),
        title: winners.length > 1 ? "Several values share the strongest emphasis" : numberMeaning(Number(winners[0])).title,
        summary: winners.length > 1
          ? "The values " + winners.join(", ") + " occur equally often, so the name has a tied emphasis rather than one dominant Hidden Passion value."
          : "Value " + winners[0] + " appears more often than any other value in the entered spelling.",
        keywords: ["name frequency", "repeated values", "emphasis"],
        counts: data.counts
      }
    );
  }

  function missingValues(input) {
    const data = frequencyData(input.full_name);
    const missing = Object.keys(data.counts).filter(function (key) { return data.counts[key] === 0; });
    return result(
      missing.length ? missing.join(" · ") : "None",
      "Karmic Lessons",
      ["Present values: " + Object.keys(data.counts).filter(function (key) { return data.counts[key] > 0; }).join(", "), "Missing values: " + (missing.join(", ") || "none")],
      [metric("Present values", 9 - missing.length), metric("Missing values", missing.length)],
      [
        section("How to read absence", "A missing value is a reflective practice area, not a defect or sentence."),
        section(
          "Themes not represented directly",
          missing.length
            ? joinNumberThemes(missing.map(Number), "overview") + (missing.length > 4 ? " The complete list remains visible above; this free reading expands the first four themes to keep the result usable." : "")
            : "Every value from 1 through 9 appears at least once, so this method does not identify a missing-value practice area for the entered spelling."
        ),
        section("Keep the context", "Name length and spelling affect which values appear. Compare this with the full Expression pattern."),
        section("Reflection prompt", "Which missing theme would be useful to practice in one small, observable way?")
      ],
      {
        interpret_primary: false,
        reading_values: [],
        title: missing.length ? "Missing values in this spelling" : "All nine values are present",
        summary: missing.length
          ? "The values " + missing.join(", ") + " do not appear in the entered spelling; they are shown as reflective practice areas, not defects."
          : "Every Pythagorean value from 1 through 9 appears at least once in the entered spelling.",
        keywords: ["letter frequency", "missing values", "name pattern"],
        counts: data.counts,
        missing: missing
      }
    );
  }

  function subconsciousSelf(input) {
    const data = frequencyData(input.full_name);
    const present = Object.keys(data.counts).filter(function (key) { return data.counts[key] > 0; });
    const missing = Object.keys(data.counts).filter(function (key) { return data.counts[key] === 0; });
    return result(
      present.length,
      "Subconscious Self Number",
      ["Present values: " + present.join(", "), "Distinct count: " + present.length],
      [metric("Distinct values", present.length), metric("Missing values", missing.join(", ") || "none")],
      [
        section("What the count represents", "This number counts how many of the nine Pythagorean values appear in the entered spelling. A higher count means broader numeric coverage in this method, not greater intelligence, resilience, or human worth."),
        section("Present and absent themes", "Present values show which symbolic qualities the spelling contains directly. Missing values remain possible human capacities; they simply are not represented by these letter values."),
        section("A grounded use", "Choose one missing or lightly represented quality that would be useful in a current situation and define a behavior through which it could be practiced.")
      ],
      {
        interpret_primary: false,
        reading_values: [],
        title: present.length + " of 9 values are present",
        summary: "The entered spelling contains " + present.length + " distinct Pythagorean values and omits " + missing.length + "; this is a structural name pattern, not a personality score.",
        keywords: ["distinct values", "name structure", "numeric coverage"],
        counts: data.counts
      }
    );
  }

  function edgeLetter(input, which) {
    const name = requireName(input.first_name, "the first name");
    const chars = letters(name);
    const letter = which === "first" ? chars[0] : chars[chars.length - 1];
    const value = pythagorean(letter);
    return result(
      value,
      which === "first" ? "Cornerstone" : "Capstone",
      [(which === "first" ? "First" : "Final") + " alphabetic letter: " + letter, letter + " = " + value],
      [metric("Letter", letter), metric("Pythagorean value", value)]
    );
  }

  function firstVowel(input) {
    const name = requireName(input.first_name, "the first name");
    const vowel = letters(name).find(function (letter) { return VOWELS.has(letter); });
    if (!vowel) throw new Error("No A, E, I, O, or U appears in this spelling. Y is not assumed to be a vowel.");
    const value = pythagorean(vowel);
    return result(value, "First Vowel", ["First conventional vowel: " + vowel, vowel + " = " + value], [metric("Letter", vowel), metric("Value", value)]);
  }

  function bridge(input) {
    const lp = lifePath(parseDate(input.birth_date, "date of birth"));
    const ex = nameNumber(input.full_name, "all");
    const lpBase = baseValue(lp.value);
    const exBase = baseValue(ex.value);
    const value = Math.abs(lpBase - exBase);
    const aligned = value === 0;
    return result(
      value,
      "Life Path–Expression Bridge",
      [
        "Life Path base: " + lp.value + " → " + lpBase,
        "Expression base: " + ex.value + " → " + exBase,
        "|" + lpBase + " − " + exBase + "| = " + value
      ],
      [metric("Life Path", lp.value), metric("Expression", ex.value), metric("Bridge", value)],
      aligned ? [
        section("Numerical alignment", "The Life Path and Expression share the same base value, so the Bridge is zero."),
        section("What zero means here", "Zero describes no numerical distance between these two chart values; it is not a separate Zero-number reading."),
        section("Reflection prompt", "Where do your direction and your way of contributing already reinforce one another?")
      ] : null,
      aligned ? {
        title: "Aligned base values",
        summary: "The two core values reduce to the same base number, so no additional bridge quality is required.",
        keywords: ["alignment", "integration", "shared base"]
      } : null
    );
  }

  function karmicDebt(input) {
    const date = parseDate(input.birth_date, "date of birth");
    const lp = lifePath(date);
    const bd = birthday(date);
    const ex = nameNumber(input.full_name, "all");
    const su = nameNumber(input.full_name, "vowels");
    const pe = nameNumber(input.full_name, "consonants");
    const debts = new Set([13, 14, 16, 19]);
    const found = [];
    const trails = [
      { source: "Life Path", data: lp },
      { source: "Birthday", data: bd },
      { source: "Expression", data: ex },
      { source: "Soul Urge", data: su },
      { source: "Personality", data: pe }
    ];
    trails.forEach(function (trail) {
      trail.data.chain.forEach(function (value) {
        if (debts.has(value)) found.push({ source: trail.source, value: value });
      });
    });
    const debtThemes = {
      13: "13/4 is traditionally associated with learning disciplined, patient construction without shortcuts or punishing overwork.",
      14: "14/5 is traditionally associated with using freedom responsibly, especially through moderation, consistency, and awareness of consequences.",
      16: "16/7 is traditionally associated with humility, reality-testing, and rebuilding understanding when an idealized self-image or assumption no longer holds.",
      19: "19/1 is traditionally associated with self-reliance that still leaves room for cooperation, give-and-take, and other people’s choices."
    };
    const foundValues = Array.from(new Set(found.map(function (item) { return item.value; })));
    return result(
      found.length ? found.map(function (item) { return item.value; }).join(" · ") : "None found",
      "Karmic Debt Check",
      trails.map(function (trail) {
        return formula(trail.source + " trail", trail.data.raw, trail.data.chain);
      }),
      found.length
        ? found.map(function (item) { return metric(item.source, item.value, "compound appears in the trail"); })
        : [metric("Debt compounds", "None", "13, 14, 16, and 19 did not appear")],
      [
        section("What was checked", "Life Path, Birthday, Expression, Soul Urge, and Personality trails were checked. Only compounds that actually appear are reported."),
        section(
          "Traditional practice themes",
          foundValues.length
            ? foundValues.map(function (value) { return debtThemes[value]; }).join(" ")
            : "No traditional debt compound appears in these five trails. That does not make a chart better, easier, or exempt from ordinary growth; it only means this specific convention found no 13, 14, 16, or 19 compound."
        ),
        section("Grounded language", "Traditional debt language is reframed as a practice theme, not guilt, punishment, or fate."),
        section("Reflection prompt", found.length ? "What disciplined, balanced action would make this theme constructive?" : "Which visible chart theme feels most useful to explore next?")
      ],
      {
        interpret_primary: false,
        reading_values: [],
        title: found.length ? "Debt compounds found in the chart trail" : "No debt compound found",
        summary: found.length
          ? "At least one of the traditional compounds 13, 14, 16, or 19 appears in a displayed core-number calculation trail."
          : "None of the traditional compounds 13, 14, 16, or 19 appears in the five displayed core-number trails.",
        keywords: ["compound totals", "five core placements", "visible calculation"],
        found: found
      }
    );
  }

  function yearResult(input) {
    const date = parseDate(input.birth_date, "date of birth");
    const year = targetYear(input.target_year);
    const py = personalYear(date, year);
    return result(
      py.value,
      "Personal Year " + year,
      [date.month + " + " + date.day + " + digits of " + year + " = " + py.raw, formula("Reduction", py.raw, py.chain)],
      [metric("Target year", year), metric("Personal Year", py.value)]
    );
  }

  function monthResult(input) {
    const date = parseDate(input.birth_date, "date of birth");
    const target = parseDate(input.target_date, "target date");
    const pm = personalMonth(date, target);
    return result(
      pm.value,
      "Personal Month",
      [
        "Personal Year " + target.year + ": " + pm.personalYear,
        pm.personalYear + " + month " + target.month + " = " + pm.raw,
        formula("Reduction", pm.raw, pm.chain)
      ],
      [metric("Personal Year", pm.personalYear), metric("Calendar month", target.month), metric("Personal Month", pm.value)]
    );
  }

  function dayResult(input) {
    const date = parseDate(input.birth_date, "date of birth");
    const target = parseDate(input.target_date, "target date");
    const pd = personalDay(date, target);
    return result(
      pd.value,
      "Personal Day",
      [
        "Personal Year: " + pd.personalYear,
        "Personal Month: " + pd.personalMonth,
        pd.personalMonth + " + day " + target.day + " = " + pd.raw,
        formula("Reduction", pd.raw, pd.chain)
      ],
      [metric("Personal Year", pd.personalYear), metric("Personal Month", pd.personalMonth), metric("Personal Day", pd.value)]
    );
  }

  function universalYear(input) {
    const year = targetYear(input.target_year);
    const raw = sumDigits(year);
    const reduced = reduceNumber(raw, false);
    return result(reduced.value, "Universal Year " + year, ["Digits of " + year + " = " + raw, formula("Single-digit reduction", raw, reduced.chain)], [metric("Calendar year", year), metric("Digit total", raw)]);
  }

  function dateComponents(date) {
    return {
      month: reduceNumber(date.month, true).value,
      day: reduceNumber(date.day, true).value,
      year: reduceNumber(sumDigits(date.year), true).value
    };
  }

  function challengeComponents(date) {
    return {
      month: reduceNumber(date.month, false).value,
      day: reduceNumber(date.day, false).value,
      year: reduceNumber(sumDigits(date.year), false).value
    };
  }

  function pinnacles(input) {
    const date = parseDate(input.birth_date, "date of birth");
    const c = dateComponents(date);
    const one = reduceNumber(c.month + c.day, true).value;
    const two = reduceNumber(c.day + c.year, true).value;
    const three = reduceNumber(one + two, true).value;
    const four = reduceNumber(c.month + c.year, true).value;
    const lp = lifePath(date);
    const firstEnd = 36 - baseValue(lp.value);
    const ranges = ["0–" + firstEnd, (firstEnd + 1) + "–" + (firstEnd + 9), (firstEnd + 10) + "–" + (firstEnd + 18), (firstEnd + 19) + "+"];
    return result(
      one + " · " + two + " · " + three + " · " + four,
      "Four Pinnacle Cycles",
      [
        "Reduced month/day/year: " + c.month + " / " + c.day + " / " + c.year,
        "P1 month+day = " + one,
        "P2 day+year = " + two,
        "P3 P1+P2 = " + three,
        "P4 month+year = " + four
      ],
      [one, two, three, four].map(function (value, index) { return metric("Pinnacle " + (index + 1), value, "ages " + ranges[index]); }),
      [
        section("Read as chapters", "These are broad symbolic emphases, not exact event predictions."),
        section("Transition age", "The first boundary uses 36 minus the base Life Path; later boundaries follow in nine-year intervals."),
        section("Reflection prompt", "Which quality from the current chapter deserves a more deliberate form?")
      ],
      {
        interpret_primary: false,
        reading_values: [one, two, three, four],
        title: "Your four Pinnacle chapters",
        summary: "The four values are displayed with their age ranges so each broad chapter can be read without hiding the transition convention.",
        keywords: ["four cycles", "age ranges", "life chapters"],
        pinnacles: [one, two, three, four],
        ranges: ranges
      }
    );
  }

  function challenges(input) {
    const date = parseDate(input.birth_date, "date of birth");
    const c = challengeComponents(date);
    const one = Math.abs(c.day - c.month);
    const two = Math.abs(c.day - c.year);
    const three = Math.abs(one - two);
    const four = Math.abs(c.month - c.year);
    const values = [one, two, three, four];
    return result(
      values.join(" · "),
      "Four Challenge Numbers",
      [
        "|day " + c.day + " − month " + c.month + "| = " + one,
        "|day " + c.day + " − year " + c.year + "| = " + two,
        "|" + one + " − " + two + "| = " + three,
        "|month " + c.month + " − year " + c.year + "| = " + four
      ],
      values.map(function (value, index) { return metric("Challenge " + (index + 1), value); }),
      [
        section("Growth, not punishment", "A Challenge names a tension that may develop skill through conscious practice."),
        section("Zero stays zero", "A zero difference is preserved and interpreted as a broad field of choice."),
        section("Reflection prompt", "What would the constructive expression of the current Challenge look like in observable behavior?")
      ],
      {
        interpret_primary: false,
        reading_values: values,
        title: "Your four Challenge values",
        summary: "All four differences use single-digit month, day, and year components; zero remains a valid result.",
        keywords: ["four challenges", "absolute differences", "growth themes"],
        challenges: values
      }
    );
  }

  function periodCycles(input) {
    const date = parseDate(input.birth_date, "date of birth");
    const c = dateComponents(date);
    return result(
      c.month + " · " + c.day + " · " + c.year,
      "Three Period Cycles",
      ["Birth month → " + c.month, "Birth day → " + c.day, "Birth year digits → " + c.year],
      [metric("First Period", c.month, "birth month"), metric("Second Period", c.day, "birth day"), metric("Third Period", c.year, "birth year")],
      [
        section("Three broad chapters", "The month, day, and year components are kept separate rather than blended into one result."),
        section("Method boundary", "Schools differ on exact transition ages, so this tool presents the sequence without inventing precise event dates."),
        section("Reflection prompt", "Which quality from the current broad period deserves a more deliberate expression?")
      ],
      {
        interpret_primary: false,
        reading_values: [c.month, c.day, c.year],
        title: "Your three Period Cycles",
        summary: "Birth month, birth day, and birth-year digits create three distinct long-term values shown in their traditional order.",
        keywords: ["three periods", "birth components", "long-term cycles"],
        periods: [c.month, c.day, c.year]
      }
    );
  }

  function activeTransit(part, age) {
    const mapped = letterValues(part);
    const cycle = mapped.reduce(function (total, item) { return total + item.value; }, 0);
    if (!cycle) throw new Error("Each name part must contain at least one Latin letter.");
    let position = ((age % cycle) + cycle) % cycle;
    for (const item of mapped) {
      if (position < item.value) return item;
      position -= item.value;
    }
    return mapped[0];
  }

  function essence(input) {
    const birth = parseDate(input.birth_date, "date of birth");
    const target = input.target_date
      ? parseDate(input.target_date, "target date")
      : parseDate(String(targetYear(input.target_year)) + "-12-31", "target date");
    if (compareDates(target, birth) < 0) {
      throw new Error("Choose a target date on or after the date of birth.");
    }
    const parts = nameParts(input.full_name);
    const age = completedAge(birth, target);
    const active = parts.map(function (part) { return activeTransit(part, age); });
    const raw = active.reduce(function (total, item) { return total + item.value; }, 0);
    const reduced = reduceNumber(raw, true);
    return result(
      reduced.value,
      "Personal Essence on " + target.iso,
      [
        "Completed age on " + target.iso + ": " + age,
        "Active letters: " + active.map(function (item) { return item.letter + "=" + item.value; }).join(" · "),
        formula("Essence reduction", raw, reduced.chain)
      ],
      active.map(function (item, index) { return metric("Name part " + (index + 1), item.letter, "value " + item.value); }),
      null,
      { active_letters: active, age: age, target_date: target.iso }
    );
  }

  function compatibilityNarrative(firstLabel, firstValue, secondLabel, secondValue) {
    const firstMeaning = numberMeaning(firstValue);
    const secondMeaning = numberMeaning(secondValue);
    return (
      firstLabel + " " + firstValue + ": " + firstMeaning.summary + " " +
      secondLabel + " " + secondValue + ": " + secondMeaning.summary + " " +
      "These themes are shown side by side; no match quality is inferred from equality, distance, or parity."
    );
  }

  function relationshipDynamics(firstValue, secondValue) {
    const firstMeaning = numberMeaning(firstValue);
    const secondMeaning = numberMeaning(secondValue);
    const sameBase = baseValue(firstValue) === baseValue(secondValue);
    return [
      section(
        "Natural points of attraction",
        "The first pattern may value " + firstMeaning.potential.toLowerCase() +
          " The second may bring " + secondMeaning.potential.toLowerCase() +
          " Attraction is a possibility to notice, not a prediction."
      ),
      section(
        "Different pace and needs",
        sameBase
          ? "These values share a base number, so their broad pace may feel familiar. Familiarity still does not mean identical needs or responses."
          : "The first pattern is described this way: " + firstMeaning.overview +
            " The second is described this way: " + secondMeaning.overview
      ),
      section(
        "Communication style",
        "Listen for the first person's need around " + firstMeaning.keywords.join(", ") +
          ", and the second person's need around " + secondMeaning.keywords.join(", ") +
          ". Ask directly instead of assigning motives from either number."
      ),
      section(
        "Where tension may start",
        "For the first pattern, " + firstMeaning.friction.toLowerCase() +
          " For the second, " + secondMeaning.friction.toLowerCase()
      ),
      section(
        "How to support the connection",
        "For the first pattern: " + firstMeaning.practice +
          " For the second: " + secondMeaning.practice
      )
    ];
  }

  function twoLifePaths(input, label) {
    const one = lifePath(parseDate(input.birth_date, "first date of birth"));
    const two = lifePath(parseDate(input.partner_birth_date, "other date of birth"));
    return result(
      one.value + " + " + two.value,
      label,
      [formula("First Life Path", one.raw, one.chain), formula("Second Life Path", two.raw, two.chain)],
      [metric("First Life Path", one.value), metric("Second Life Path", two.value)],
      [
        section("Life Paths side by side", compatibilityNarrative("First Life Path", one.value, "Other Life Path", two.value)),
        ...relationshipDynamics(one.value, two.value),
        section("How to use this", "Similarity and difference can each be lived constructively or poorly. Behavior and direct communication supply the relationship evidence."),
        section("Conversation prompt", "What helps each person feel respected when your natural pacing or priorities differ?")
      ],
      {
        interpret_primary: false,
        reading_values: [one.value, two.value],
        title: "Two Life Paths, read side by side",
        summary: "Both Life Path themes remain visible and independent; the calculator does not convert their distance or parity into a compatibility verdict.",
        keywords: ["two Life Paths", "side-by-side themes", "no match score"],
        first: one.value,
        second: two.value
      }
    );
  }

  function compatibility(input) {
    const firstDate = parseDate(input.birth_date, "first date of birth");
    const secondDate = parseDate(input.partner_birth_date, "other date of birth");
    const lp1 = lifePath(firstDate);
    const lp2 = lifePath(secondDate);
    const bd1 = birthday(firstDate);
    const bd2 = birthday(secondDate);
    const relationship = reduceNumber(baseValue(lp1.value) + baseValue(lp2.value), false).value;
    return result(
      lp1.value + " ↔ " + lp2.value,
      "Compatibility Pattern",
      [
        formula("First Life Path", lp1.raw, lp1.chain),
        formula("Second Life Path", lp2.raw, lp2.chain),
        baseValue(lp1.value) + " + " + baseValue(lp2.value) + " → relationship " + relationship
      ],
      [metric("First Life Path", lp1.value), metric("Second Life Path", lp2.value), metric("First Birthday", bd1.value), metric("Second Birthday", bd2.value), metric("Relationship Number", relationship)],
      [
        section("Life Paths side by side", compatibilityNarrative("First Life Path", lp1.value, "Other Life Path", lp2.value)),
        section("Birthdays side by side", compatibilityNarrative("First Birthday", bd1.value, "Other Birthday", bd2.value)),
        ...relationshipDynamics(lp1.value, lp2.value),
        section("Conversation prompt", "Where do you already translate your different needs well—and where do assumptions replace a direct question?")
      ],
      {
        interpret_primary: false,
        reading_values: [lp1.value, lp2.value, bd1.value, bd2.value, relationship],
        title: "A five-part relationship pattern",
        summary: "Two Life Paths, two Birthday Numbers, and one disclosed Relationship Number are shown without an invented compatibility percentage.",
        keywords: ["Life Paths", "Birthday Numbers", "Relationship Number"],
        first: { life_path: lp1.value, birthday: bd1.value },
        second: { life_path: lp2.value, birthday: bd2.value },
        relationship: relationship
      }
    );
  }

  function nameCompatibility(input) {
    const oneName = requireName(input.full_name, "the first name");
    const twoName = requireName(input.partner_name, "the other name");
    const one = {
      expression: nameNumber(oneName, "all").value,
      soul: nameNumber(oneName, "vowels").value,
      personality: nameNumber(oneName, "consonants").value
    };
    const two = {
      expression: nameNumber(twoName, "all").value,
      soul: nameNumber(twoName, "vowels").value,
      personality: nameNumber(twoName, "consonants").value
    };
    return result(
      one.expression + " ↔ " + two.expression,
      "Name Compatibility Pattern",
      [
        "Expression: " + one.expression + " ↔ " + two.expression,
        "Soul Urge: " + one.soul + " ↔ " + two.soul,
        "Personality: " + one.personality + " ↔ " + two.personality
      ],
      [metric("Expression", one.expression + " / " + two.expression), metric("Soul Urge", one.soul + " / " + two.soul), metric("Personality", one.personality + " / " + two.personality)],
      [
        section("Expression side by side", compatibilityNarrative("First Expression", one.expression, "Other Expression", two.expression)),
        section("Soul Urge side by side", compatibilityNarrative("First Soul Urge", one.soul, "Other Soul Urge", two.soul)),
        section("Personality side by side", compatibilityNarrative("First Personality", one.personality, "Other Personality", two.personality)),
        section("Conversation prompt", "Which inner need is easy to miss because the other person's outer style looks different?")
      ],
      {
        interpret_primary: false,
        reading_values: [one.expression, one.soul, one.personality, two.expression, two.soul, two.personality],
        title: "Three name layers, side by side",
        summary: "Expression, Soul Urge, and Personality values are compared independently without turning similarity into a score.",
        keywords: ["Expression", "Soul Urge", "Personality"],
        first: one,
        second: two
      }
    );
  }

  function relationshipNumber(input) {
    const first = lifePath(parseDate(input.birth_date, "first date of birth"));
    const second = lifePath(parseDate(input.partner_birth_date, "other date of birth"));
    const raw = baseValue(first.value) + baseValue(second.value);
    const reduced = reduceNumber(raw, false);
    return result(
      reduced.value,
      "Relationship Number",
      [
        first.value + " → base " + baseValue(first.value),
        second.value + " → base " + baseValue(second.value),
        formula("Combined relationship value", raw, reduced.chain)
      ],
      [metric("First Life Path", first.value), metric("Second Life Path", second.value), metric("Relationship Number", reduced.value)]
    );
  }

  function weddingDate(input) {
    const event = parseDate(input.event_date, "event date");
    const firstDate = parseDate(input.birth_date, "first date of birth");
    const secondDate = parseDate(input.partner_birth_date, "other date of birth");
    if (compareDates(event, firstDate) < 0 || compareDates(event, secondDate) < 0) {
      throw new Error("Choose an event date on or after both dates of birth.");
    }
    const eventData = lifePath(event);
    const first = lifePath(firstDate);
    const second = lifePath(secondDate);
    return result(
      eventData.value,
      "Wedding Date Number",
      [
        dateDigitFormula(event, "Event-date digits"),
        formula("Event-date reduction", eventData.raw, eventData.chain),
        formula("First Life Path", first.raw, first.chain),
        formula("Other Life Path", second.raw, second.chain)
      ],
      [metric("Event Date", eventData.value), metric("First Life Path", first.value), metric("Second Life Path", second.value)],
      [
        section("Symbolic tone", numberMeaning(eventData.value).summary),
        section("Event and first partner", compatibilityNarrative("Event date", eventData.value, "First Life Path", first.value)),
        section("Event and other partner", compatibilityNarrative("Event date", eventData.value, "Other Life Path", second.value)),
        section("Grounded choice", "Practical availability, access, budget, safety, and shared values matter more than numeric alignment.")
      ],
      {
        interpret_primary: false,
        reading_values: [eventData.value, first.value, second.value],
        title: "Wedding date " + eventData.value + " in context",
        summary: "The event-date theme is shown beside both Life Paths as a conversation lens, never as a prediction of marital success.",
        keywords: ["event date", "two Life Paths", "grounded choice"],
        event_number: eventData.value,
        first_life_path: first.value,
        second_life_path: second.value
      }
    );
  }

  function nameProfile(input, sourceKey) {
    const source = input[sourceKey || "name_to_test"];
    const name = requireName(source);
    const ex = nameNumber(name, "all");
    const su = nameNumber(name, "vowels");
    const pe = nameNumber(name, "consonants");
    const initial = initialData(name);
    const chars = letters(name);
    const firstLetter = chars[0];
    const lastLetter = chars[chars.length - 1];
    const frequencies = frequencyData(name);
    const missing = Object.keys(frequencies.counts).filter(function (key) { return frequencies.counts[key] === 0; });
    const repeated = Object.keys(frequencies.counts).filter(function (key) { return frequencies.counts[key] > 1; });
    return result(
      ex.value,
      "Name Numerology Profile",
      [
        formula("Expression", ex.raw, ex.chain),
        formula("Soul Urge", su.raw, su.chain),
        formula("Personality", pe.raw, pe.chain),
        "Initials: " + initial.mapped.map(function (item) { return item.letter + "=" + item.value; }).join(" · "),
        formula("Balance from initials", initial.raw, initial.chain),
        "First / last letters: " + firstLetter + "=" + pythagorean(firstLetter) + " / " + lastLetter + "=" + pythagorean(lastLetter),
        "Letter-value frequencies: " + Object.keys(frequencies.counts).map(function (key) { return key + ":" + frequencies.counts[key]; }).join(" · ")
      ],
      [
        metric("Expression", ex.value),
        metric("Soul Urge", su.mapped.length ? su.value : "No A/E/I/O/U"),
        metric("Personality", pe.mapped.length ? pe.value : "No consonants"),
        metric("Initials / Balance", initial.mapped.map(function (item) { return item.letter; }).join("") + " / " + initial.value),
        metric("First / last", firstLetter + " / " + lastLetter),
        metric("Repeated values", repeated.map(function (key) { return key + "×" + frequencies.counts[key]; }).join(", ") || "none"),
        metric("Missing values", missing.join(", ") || "none")
      ],
      [
        section("Full-name expression", numberMeaning(ex.value).summary),
        section(
          "Inner and outer layers",
          (su.mapped.length ? "Soul Urge " + su.value + " describes the vowel layer. " : "No A, E, I, O, or U appears, so this method has no conventional vowel layer to calculate. ") +
          (pe.mapped.length ? "Personality " + pe.value + " describes the consonant layer." : "No consonants appear, so this spelling has no Personality total under the stated method.")
        ),
        section("Name structure", "Initials, first and last letters, repeated values, and missing values are shown separately so you can compare them."),
        section("Reflection prompt", "Which name layer feels immediately familiar, and which one adds useful complexity?")
      ],
      {
        interpret_primary: false,
        reading_values: [ex.value, su.value, pe.value, initial.value, pythagorean(firstLetter), pythagorean(lastLetter)],
        title: "A layered reading of this name",
        summary: "Expression, Soul Urge, Personality, initials, edge letters, repetitions, and missing values are kept distinct so one total never has to explain the whole name.",
        keywords: ["name profile", "inner and outer layers", "letter structure"],
        profile: {
          expression: ex.value,
          soul_urge: su.mapped.length ? su.value : null,
          personality: pe.mapped.length ? pe.value : null,
          initials: initial.mapped,
          balance: initial.value,
          first_letter: firstLetter,
          last_letter: lastLetter,
          frequencies: frequencies.counts,
          repeated: repeated,
          missing: missing
        }
      }
    );
  }

  function chaldeanName(input) {
    const data = nameNumber(input.name_to_test, "all", "chaldean");
    const baseMeaning = numberMeaning(data.value);
    return result(
      data.raw + "/" + data.value,
      "Chaldean Name Number",
      [
        "Mapping: " + data.mapped.map(function (item) { return item.letter + "=" + item.value; }).join(" · "),
        "Compound total: " + data.raw,
        formula("Base reduction", data.raw, data.chain)
      ],
      [metric("Compound", data.raw), metric("Base number", data.value), metric("Letters", data.mapped.length)],
      [
        section("Compound first", "The Chaldean compound is " + data.raw + ". It is kept visible rather than silently replaced by its root."),
        section("Base theme", "The single-digit base is " + data.value + ". " + baseMeaning.summary),
        section("Method boundary", "The compound and base belong to the stated Chaldean mapping. They are not blended with a Pythagorean name score.")
      ],
      {
        interpret_primary: false,
        reading_values: [data.value],
        title: "Compound " + data.raw + " · Base " + data.value,
        summary: "The name totals " + data.raw + " in the Chaldean 1–8 letter table and reduces to the base value " + data.value + ".",
        keywords: ["Chaldean compound", "base " + data.value],
        calculation: data
      }
    );
  }

  function businessName(input) {
    const p = nameNumber(input.business_name, "all");
    const c = nameNumber(input.business_name, "all", "chaldean");
    return result(
      p.value + " / " + c.value,
      "Business Name Profile",
      [formula("Pythagorean", p.raw, p.chain), formula("Chaldean", c.raw, c.chain)],
      [metric("Pythagorean", p.value, "compound " + p.raw), metric("Chaldean", c.value, "compound " + c.raw)],
      [
        section("Keep systems separate", "The two values come from different mappings and should not be blended into one score."),
        section("Creative use", "Treat these themes as language for brand character, then validate the name with customers and legal research."),
        section("Reality check", "No name number predicts revenue, product-market fit, trademark availability, or business success.")
      ],
      {
        interpret_primary: false,
        reading_values: [p.value, c.value],
        title: "Two name systems, kept separate",
        summary: "The brand spelling is calculated once with the Pythagorean table and once with the Chaldean table so the methods never collapse into one score.",
        keywords: ["Pythagorean", "Chaldean", "brand spelling"],
        pythagorean: { compound: p.raw, value: p.value },
        chaldean: { compound: c.raw, value: c.value }
      }
    );
  }

  function mixedIdentifier(value, label) {
    const normalized = String(value || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (!normalized) throw new Error("Enter a " + label + ".");
    const parts = normalized.split("").map(function (character) {
      return { character: character, value: /\d/.test(character) ? Number(character) : pythagorean(character) };
    });
    const raw = parts.reduce(function (total, item) { return total + item.value; }, 0);
    const reduced = reduceNumber(raw, true);
    return result(
      reduced.value,
      label,
      [parts.map(function (item) { return item.character + "=" + item.value; }).join(" · "), formula("Reduction", raw, reduced.chain)],
      [metric("Normalized", normalized), metric("Compound total", raw)]
    );
  }

  function phoneNumber(input) {
    const normalized = digits(input.phone_number);
    if (normalized.length < 7 || normalized.length > 18) throw new Error("Enter a phone number with 7–18 digits.");
    const raw = sumDigits(normalized);
    const reduced = reduceNumber(raw, true);
    return result(
      reduced.value,
      "Phone Number Pattern",
      ["Digits used: " + normalized.split("").join(" + "), formula("Reduction", raw, reduced.chain)],
      [metric("Digits counted", normalized.length), metric("Compound total", raw)]
    );
  }

  function luckyDate(input) {
    const birth = parseDate(input.birth_date, "date of birth");
    const event = parseDate(input.event_date, "event date");
    if (compareDates(event, birth) < 0) {
      throw new Error("Choose an event date on or after the date of birth.");
    }
    const eventNumber = lifePath(event);
    const pd = personalDay(birth, event);
    return result(
      eventNumber.value + " / " + pd.value,
      "Date Alignment",
      [formula("Event-date number", eventNumber.raw, eventNumber.chain), "Personal Year " + pd.personalYear + " → Month " + pd.personalMonth + " → Day " + pd.value],
      [metric("Event-date number", eventNumber.value), metric("Personal Year", pd.personalYear), metric("Personal Month", pd.personalMonth), metric("Personal Day", pd.value)],
      [
        section("Two different lenses", "The event-date number belongs to the date itself; the Personal Day belongs to your cycle on that date."),
        section("No luck score", "Numeric correspondences remain visible, but they are not treated as improved odds or a guarantee."),
        section("Grounded choice", "Use practical availability, evidence, readiness, and professional guidance for important timing.")
      ],
      {
        interpret_primary: false,
        reading_values: [eventNumber.value, pd.value],
        title: "The date and your cycle, side by side",
        summary: "The event-date value and your Personal Day are separate calculations; neither is converted into a probability or luck percentage.",
        keywords: ["event date", "Personal Day", "no luck score"],
        event_number: eventNumber.value,
        personal_day: pd.value
      }
    );
  }

  function luckyNumbers(input) {
    const date = parseDate(input.birth_date, "date of birth");
    const year = targetYear(input.target_year);
    const lp = lifePath(date).value;
    const bd = birthday(date).value;
    const at = attitude(date).value;
    const py = personalYear(date, year).value;
    const set = Array.from(new Set([lp, bd, at, py]));
    return result(
      set.join(" · "),
      "Personal Number Set",
      ["Life Path: " + lp, "Birthday: " + bd, "Attitude: " + at, "Personal Year " + year + ": " + py],
      [metric("Life Path", lp), metric("Birthday", bd), metric("Attitude", at), metric("Personal Year " + year, py)],
      [
        section("Why these numbers", "Every value comes from a named, visible chart calculation rather than random generation."),
        section("Repeated values", set.length < 4 ? "At least one number repeats across the selected chart layers." : "Each selected chart layer contributes a different value."),
        section("Probability note", "This set does not improve gambling, lottery, investment, or game odds.")
      ],
      {
        interpret_primary: false,
        reading_values: set,
        title: "A traceable personal number set",
        summary: "Every value comes from a named birth-date calculation and the explicitly selected year; no number is randomly generated.",
        keywords: ["Life Path", "Birthday", "Personal Year"],
        target_year: year,
        values: set
      }
    );
  }

  function loShu(input) {
    const date = parseDate(input.birth_date, "date of birth");
    const source = date.iso.replace(/-/g, "").split("").map(Number).filter(function (number) { return number !== 0; });
    const counts = {};
    for (let value = 1; value <= 9; value += 1) counts[value] = 0;
    source.forEach(function (number) { counts[number] += 1; });
    const missing = Object.keys(counts).filter(function (key) { return counts[key] === 0; });
    const repeated = Object.keys(counts).filter(function (key) { return counts[key] > 1; });
    const grid = [[4, 9, 2], [3, 5, 7], [8, 1, 6]];
    const lines = [
      [4, 9, 2], [3, 5, 7], [8, 1, 6],
      [4, 3, 8], [9, 5, 1], [2, 7, 6],
      [4, 5, 6], [2, 5, 8]
    ];
    const completeLines = lines
      .filter(function (line) { return line.every(function (number) { return counts[number] > 0; }); })
      .map(function (line) { return line.join("-"); });
    return result(
      "Grid ready",
      "Lo Shu Grid",
      ["Nonzero birth-date digits: " + source.join(" · "), "Traditional positions: 4-9-2 / 3-5-7 / 8-1-6"],
      [
        metric("Repeated digits", repeated.join(", ") || "none"),
        metric("Missing digits", missing.join(", ") || "none"),
        metric("Complete lines", completeLines.join(", ") || "none")
      ],
      [
        section("Read the raw grid first", "Repetition and absence are visible before any interpretation is added."),
        section(
          "Repeated themes",
          repeated.length
            ? joinNumberThemes(repeated.map(Number), "potential") + " Repetition is treated as emphasis in the date pattern, not proof that a quality is stronger or always expressed well."
            : "No nonzero digit repeats in this birth date, so the grid contains no repeated-value emphasis under this convention."
        ),
        section(
          "Missing themes",
          missing.length
            ? joinNumberThemes(missing.map(Number), "overview") + (missing.length > 4 ? " The full missing set remains visible in the grid while this free reading expands the first four themes." : "") + " An absent digit is a possible practice area, not an absent human capacity."
            : "Every value from 1 through 9 is represented at least once. This is structural completeness in the grid, not a claim of personal perfection."
        ),
        section("Complete lines", completeLines.length ? "Every position is present in: " + completeLines.join(", ") + "." : "None of the eight horizontal, vertical, or diagonal lines is complete."),
        section("Zero convention", "Zero is omitted because the traditional square contains 1 through 9."),
        section("Reflection prompt", "Which repeated theme feels familiar, and which absent one would be useful to practice?")
      ],
      {
        interpret_primary: false,
        reading_values: [],
        title: "Your Lo Shu digit grid",
        summary: "The grid shows repetitions, missing digits, and all eight possible complete lines without reducing the date to a separate scalar meaning.",
        keywords: ["Lo Shu", "birth-date digits", "complete lines"],
        grid: grid,
        counts: counts,
        complete_lines: completeLines,
        source_digits: source,
        render: "lo-shu"
      }
    );
  }

  function shortestRepeatingChunk(value) {
    for (let size = 1; size <= Math.floor(value.length / 2); size += 1) {
      if (value.length % size !== 0) continue;
      const chunk = value.slice(0, size);
      const repeats = value.length / size;
      if (chunk.repeat(repeats) === value) {
        return { chunk: chunk, repeats: repeats };
      }
    }
    return null;
  }

  function angelNumber(input) {
    const normalized = digits(input.number_pattern);
    if (normalized.length < 2 || normalized.length > 12) throw new Error("Enter a pattern containing 2–12 digits.");
    const counts = {};
    normalized.split("").forEach(function (digit) { counts[digit] = (counts[digit] || 0) + 1; });
    const raw = sumDigits(normalized);
    const reduced = reduceNumber(raw, true);
    const reverse = normalized.split("").reverse().join("");
    const repeated = Object.keys(counts).filter(function (key) { return counts[key] > 1; }).sort();
    const palindrome = normalized === reverse;
    const repeatingChunk = shortestRepeatingChunk(normalized);
    const patternParts = [];
    if (palindrome) patternParts.push("palindrome");
    if (repeatingChunk) patternParts.push("chunk " + repeatingChunk.chunk + " × " + repeatingChunk.repeats);
    if (!patternParts.length && repeated.length) patternParts.push("repeated digits");
    if (!patternParts.length) patternParts.push("mixed sequence");
    const rootMeaning = numberMeaning(reduced.value);
    return result(
      normalized,
      "Number Pattern",
      [
        "Digit sum: " + normalized.split("").join(" + ") + " = " + raw,
        formula("Root", raw, reduced.chain),
        "Pattern: " + patternParts.join(" · ")
      ],
      [
        metric("Root number", reduced.value),
        metric("Repeated digits", repeated.join(", ") || "none"),
        metric("Palindrome", palindrome ? "yes" : "no"),
        metric("Shortest repeating chunk", repeatingChunk ? repeatingChunk.chunk + " × " + repeatingChunk.repeats : "none")
      ],
      [
        section("Root theme", rootMeaning.summary),
        section("Why it stands out", repeated.length ? "Repetition makes the sequence visually memorable; context determines what it means to you." : "The sequence is mixed, so the reduced root is the clearest numeric theme."),
        section("Context prompt", "What were you thinking, feeling, or doing when this pattern captured your attention?")
      ],
      {
        interpret_primary: false,
        reading_values: [reduced.value],
        title: rootMeaning.title,
        summary: rootMeaning.summary,
        keywords: rootMeaning.keywords,
        root: reduced.value,
        counts: counts,
        palindrome: palindrome,
        repeating_chunk: repeatingChunk
      }
    );
  }

  function mirrorHour(input) {
    const match = /^(\d{2}):(\d{2})$/.exec(String(input.mirror_time || ""));
    if (!match) throw new Error("Enter a valid time.");
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    if (hour > 23 || minute > 59) throw new Error("Enter a valid 24-hour time.");
    const h = match[1];
    const m = match[2];
    const pattern = h === m ? "exact mirror" : h.split("").reverse().join("") === m ? "reversed mirror" : h.charAt(0) === h.charAt(1) && m.charAt(0) === m.charAt(1) ? "double pair" : "non-mirror time";
    const raw = sumDigits(h + m);
    const reduced = reduceNumber(raw, true);
    return result(
      h + ":" + m,
      "Mirror Hour",
      ["Pattern class: " + pattern, h.split("").concat(m.split("")).join(" + ") + " = " + raw, formula("Root", raw, reduced.chain)],
      [metric("Pattern", pattern), metric("Root number", reduced.value)],
      [
        section("Root theme", numberMeaning(reduced.value).summary),
        section("Pattern context", "The symmetry explains why the time is visually salient; it does not prove an external message."),
        section("Reflection prompt", "What in the current moment deserves a calm, reality-based check-in?")
      ],
      {
        interpret_primary: false,
        reading_values: [reduced.value],
        title: pattern.charAt(0).toUpperCase() + pattern.slice(1) + " · root " + reduced.value,
        summary: "The clock pattern is classified as " + pattern + " and its digits reduce to the numerology root " + reduced.value + ".",
        keywords: [pattern, "root " + reduced.value, "clock pattern"],
        pattern: pattern,
        root: reduced.value
      }
    );
  }

  const ALGORITHMS = {
    full_chart: fullChart,
    life_path: function (input) { return singleDateNumber(input, "life_path"); },
    birthday: function (input) { return singleDateNumber(input, "birthday"); },
    attitude: function (input) { return singleDateNumber(input, "attitude"); },
    expression: function (input) { return nameSingle(input, "all", "Expression / Destiny Number"); },
    soul_urge: function (input) { return nameSingle(input, "vowels", "Soul Urge Number"); },
    personality: function (input) { return nameSingle(input, "consonants", "Personality Number"); },
    maturity: maturity,
    balance: initials,
    rational_thought: rationalThought,
    hidden_passion: hiddenPassion,
    karmic_lessons: missingValues,
    karmic_debt: karmicDebt,
    subconscious_self: subconsciousSelf,
    cornerstone: function (input) { return edgeLetter(input, "first"); },
    capstone: function (input) { return edgeLetter(input, "last"); },
    first_vowel: firstVowel,
    bridge: bridge,
    personal_year: yearResult,
    personal_month: monthResult,
    personal_day: dayResult,
    universal_year: universalYear,
    pinnacles: pinnacles,
    challenges: challenges,
    period_cycles: periodCycles,
    essence: essence,
    compatibility: compatibility,
    life_path_compatibility: function (input) { return twoLifePaths(input, "Life Path Compatibility"); },
    name_compatibility: nameCompatibility,
    relationship_number: relationshipNumber,
    wedding_date: weddingDate,
    name_profile: function (input) { return nameProfile(input, "name_to_test"); },
    chaldean_name: chaldeanName,
    baby_name: function (input) { return nameProfile(input, "baby_name"); },
    business_name: businessName,
    house_number: function (input) { return mixedIdentifier(input.address_number, "House Number"); },
    phone_number: phoneNumber,
    vehicle_number: function (input) { return mixedIdentifier(input.vehicle_number, "Vehicle Number"); },
    lucky_date: luckyDate,
    lucky_numbers: luckyNumbers,
    lo_shu: loShu,
    angel_number: angelNumber,
    mirror_hour: mirrorHour,
    ideal_partner: idealPartner,
    partner_gallery: partnerGallery
  };

  function formatSegmentedDateParts(parts) {
    const year = String(parts.year || "").trim();
    const month = String(parts.month || "").trim();
    const day = String(parts.day || "").trim();
    if (!year && !month && !day) return "";
    if (!year || !month || !day) return year + "-" + month + "-" + day;
    return year.padStart(4, "0") + "-" + month.padStart(2, "0") + "-" + day.padStart(2, "0");
  }

  function segmentedDateValue(wrapper) {
    const parts = {};
    wrapper.querySelectorAll("[data-lc-date-part]").forEach(function (field) {
      parts[field.getAttribute("data-lc-date-part")] = field.value;
    });
    return formatSegmentedDateParts(parts);
  }

  function collect(form) {
    const values = {};
    form.querySelectorAll("[data-lc-field]").forEach(function (field) {
      values[field.getAttribute("data-lc-field")] = field.value;
    });
    form.querySelectorAll("[data-lc-date-field]").forEach(function (wrapper) {
      values[wrapper.getAttribute("data-lc-date-field")] = segmentedDateValue(wrapper);
    });
    return values;
  }

  function mountSegmentedDates(form) {
    form.querySelectorAll("[data-lc-date-field]").forEach(function (wrapper) {
      const fields = ["month", "day", "year"].map(function (part) {
        return wrapper.querySelector('[data-lc-date-part="' + part + '"]');
      }).filter(Boolean);
      fields.forEach(function (field, index) {
        field.addEventListener("input", function () {
          const limit = Number(field.getAttribute("maxlength")) || (field.getAttribute("data-lc-date-part") === "year" ? 4 : 2);
          if (String(field.value || "").length >= limit && fields[index + 1]) {
            fields[index + 1].focus();
            if (typeof fields[index + 1].select === "function") fields[index + 1].select();
          }
        });
        field.addEventListener("keydown", function (event) {
          if (event.key !== "Backspace" || field.value || !fields[index - 1]) return;
          event.preventDefault();
          const previous = fields[index - 1];
          previous.focus();
          if (typeof previous.setSelectionRange === "function") {
            const end = String(previous.value || "").length;
            previous.setSelectionRange(end, end);
          }
        });
      });
    });
  }

  function dateFieldKeyFromError(message) {
    const normalized = String(message || "").toLowerCase();
    if (normalized.includes("target date")) return "target_date";
    if (normalized.includes("event date")) return "event_date";
    if (normalized.includes("other date of birth")) return "partner_birth_date";
    if (normalized.includes("first date of birth") || normalized.includes("date of birth")) {
      return "birth_date";
    }
    return "";
  }

  function markInvalidField(form, message) {
    const dateKey = dateFieldKeyFromError(message);
    const dateWrapper = dateKey
      ? form.querySelector('[data-lc-date-field="' + dateKey + '"]')
      : null;
    if (dateWrapper) {
      dateWrapper.setAttribute("aria-invalid", "true");
      const parts = Array.from(dateWrapper.querySelectorAll("[data-lc-date-part]"));
      parts.forEach(function (field) { field.setAttribute("aria-invalid", "true"); });
      return parts[0] || dateWrapper;
    }
    const first = form.querySelector(":invalid, [data-lc-field], [data-lc-date-part]");
    if (first) first.setAttribute("aria-invalid", "true");
    return first;
  }

  function make(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = String(text);
    return node;
  }

  function emitLifecycle(root, eventName, eventContext) {
    root.dispatchEvent(new CustomEvent("luvire:" + eventName, {
      bubbles: true,
      detail: {
        calculator_key: root.getAttribute("data-calculator-key"),
        algorithm_version: VERSION,
        placement: root.getAttribute("data-placement") || "standalone",
        event_context: eventContext || ""
      }
    }));
  }

  function resultShareText(output) {
    return [
      output.label + ": " + output.primary,
      output.title + ".",
      output.summary,
      "Calculated privately with the LUVIRE " + output.algorithm_version + " method."
    ].join(" ");
  }

  function copyResultText(text, button) {
    function confirmCopy() {
      const previous = button.textContent;
      button.textContent = "Copied";
      window.setTimeout(function () { button.textContent = previous; }, 1800);
    }
    if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
      navigator.clipboard.writeText(text).then(confirmCopy).catch(function () {
        button.textContent = "Copy unavailable";
      });
      return;
    }
    const field = document.createElement("textarea");
    field.value = text;
    field.setAttribute("readonly", "");
    field.style.position = "fixed";
    field.style.opacity = "0";
    document.body.appendChild(field);
    field.select();
    const copied = typeof document.execCommand === "function" && document.execCommand("copy");
    field.remove();
    button.textContent = copied ? "Copied" : "Copy unavailable";
    if (copied) window.setTimeout(function () { button.textContent = "Copy result"; }, 1800);
  }

  function renderGrid(container, output) {
    const wrap = make("div", "lc-lo-shu");
    output.grid.forEach(function (row) {
      row.forEach(function (number) {
        const cell = make("div", "lc-lo-shu__cell");
        const count = output.counts[number] || 0;
        cell.appendChild(make("span", "lc-lo-shu__number", String(number).repeat(count) || "—"));
        cell.appendChild(make("span", "lc-lo-shu__label", count ? count + (count === 1 ? " occurrence" : " occurrences") : "missing"));
        wrap.appendChild(cell);
      });
    });
    container.appendChild(wrap);
  }

  function renderResult(root, output) {
    const target = root.querySelector("[data-lc-result]");
    const offer = root.querySelector("[data-lc-offer]");
    const isLove = root.dataset.calculatorKey === "ideal_partner";
    const isPartnerGallery = root.dataset.calculatorKey === "who_is_your_partner";
    target.classList.remove("is-visible");
    target.textContent = "";
    target.hidden = false;

    if (isPartnerGallery) {
      const section = root.closest(".luvire-calculator-page-section");
      const selector = '[data-lc-result-gallery-art="' + output.primary + '"]';
      const localSources = Array.from(root.querySelectorAll(selector));
      const themeSources = section ? Array.from(section.querySelectorAll('[data-lc-theme-result-gallery-art="' + output.primary + '"]')) : [];
      const sources = (localSources.length ? localSources : themeSources).sort(function (a, b) {
        return Number(a.getAttribute("data-index")) - Number(b.getAttribute("data-index"));
      });
      if (!sources.length) throw new Error("The illustrated partner reading is unavailable for this birth number.");

      const figure = document.createElement("figure");
      figure.className = "lc-result-gallery";
      const bar = make("div", "lc-result-gallery__bar");
      const headingWrap = make("div", "lc-result-gallery__heading");
      const resultHeading = make("h2", "lc-result-gallery__title", "Your partner carries Birth Number " + output.primary);
      resultHeading.setAttribute("tabindex", "-1");
      headingWrap.appendChild(resultHeading);
      bar.appendChild(headingWrap);
      figure.appendChild(bar);

      const pages = make("div", "lc-result-gallery__pages");
      sources.forEach(function (source, index) {
        const sourceUrl = source.getAttribute("data-src");
        if (!sourceUrl) return;
        const chapter = document.createElement("article");
        chapter.className = "lc-result-gallery__chapter";
        chapter.style.setProperty("--lc-gallery-order", String(index));
        const badge = make("span", "lc-result-gallery__chapter-number", String(index + 1).padStart(2, "0") + " / " + String(sources.length).padStart(2, "0"));
        const image = document.createElement("img");
        image.className = "lc-result-gallery__image";
        image.src = sourceUrl;
        image.alt = "Illustrated partner reading for Birth Number " + output.primary + ", chapter " + (index + 1) + " of " + sources.length;
        image.width = 1054;
        image.height = 1492;
        image.decoding = "async";
        image.loading = index === 0 ? "eager" : "lazy";
        chapter.append(badge, image);
        pages.appendChild(chapter);
      });
      figure.appendChild(pages);
      const endMarker = make("span", "lc-result-gallery__end");
      endMarker.setAttribute("data-lc-gallery-end", "");
      endMarker.setAttribute("aria-hidden", "true");
      figure.appendChild(endMarker);
      figure.appendChild(make("figcaption", "visually-hidden", "Seven illustrated chapters for the Birth Number " + output.primary + " partner archetype."));
      target.appendChild(figure);
      void target.offsetWidth;
      target.classList.add("is-visible");
      if (offer) offer.hidden = false;
      const galleryStatus = root.querySelector("[data-lc-status]");
      if (galleryStatus) galleryStatus.textContent = "Result ready. Birth number " + output.primary + ", seven illustrated chapters.";
      requestAnimationFrame(function () {
        resultHeading.focus({ preventScroll: true });
        target.scrollIntoView({
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
          block: "start"
        });
      });
      return;
    }

    if (isLove) {
      const artKey = output.reading_version + "-" + output.primary;
      const section = root.closest(".luvire-calculator-page-section");
      const localSource = root.querySelector('[data-lc-result-art="' + artKey + '"]');
      const themeSource = section && section.querySelector('[data-lc-theme-result-art="' + artKey + '"]');
      const source = localSource || themeSource;
      const sourceUrl = source && source.getAttribute("data-src");
      const figure = document.createElement("figure");
      figure.className = "lc-result-art";
      const bar = make("div", "lc-result-art__bar");
      const resultHeading = make("h2", "lc-result-art__title", "Your illustrated partner profile");
      resultHeading.setAttribute("tabindex", "-1");
      bar.appendChild(resultHeading);
      bar.appendChild(make(
        "span",
        "lc-result-art__root",
        "Birth number " + output.primary + " · " + (output.reading_version === "woman" ? "Woman" : "Man")
      ));
      figure.appendChild(bar);
      if (sourceUrl) {
        const imageLink = document.createElement("a");
        imageLink.className = "lc-result-art__link";
        imageLink.href = sourceUrl;
        imageLink.target = "_blank";
        imageLink.setAttribute("aria-label", "Open result full size");
        const resultImage = document.createElement("img");
        resultImage.className = "lc-result-art__image";
        resultImage.src = sourceUrl;
        resultImage.alt = output.reading_version + " partner reading for birth number " + output.primary;
        resultImage.width = 1024;
        resultImage.height = 1536;
        imageLink.appendChild(resultImage);
        figure.appendChild(imageLink);
      }
      const caption = make(
        "figcaption",
        "visually-hidden",
        "Birth number " + output.primary + ", " + output.reading_version + " reading: "
          + output.title + ". " + output.summary + " Ideal partner qualities: "
          + (output.ideal_partner_traits || []).join(", ") + "."
      );
      figure.appendChild(caption);
      target.appendChild(figure);
      void target.offsetWidth;
      target.classList.add("is-visible");
      if (offer) offer.hidden = false;
      const loveStatus = root.querySelector("[data-lc-status]");
      loveStatus.textContent = "Result ready. Birth number " + output.primary + ".";
      requestAnimationFrame(function () {
        resultHeading.focus({ preventScroll: true });
        target.scrollIntoView({
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
          block: "start"
        });
      });
      return;
    }

    const hero = make("div", "lc-result-hero");
    const label = make("p", "lc-result-kicker", output.label);
    const primary = make("p", "lc-result-number", output.primary);
    const title = make("h2", "lc-result-title", output.title);
    title.setAttribute("tabindex", "-1");
    const summary = make("p", "lc-result-summary", output.summary);
    hero.append(label, primary, title, summary);
    if (output.keywords && output.keywords.length) {
      const chips = make("div", "lc-result-chips");
      output.keywords.forEach(function (word) { chips.appendChild(make("span", "", word)); });
      hero.appendChild(chips);
    }
    target.appendChild(hero);

    if (output.render === "lo-shu") renderGrid(target, output);

    if (output.metrics && output.metrics.length) {
      const metrics = make("div", "lc-result-metrics");
      output.metrics.forEach(function (item, index) {
        const card = make("div", "lc-result-metric");
        card.style.setProperty("--lc-item-order", String(index));
        card.appendChild(make("span", "lc-result-metric__label", item.label));
        card.appendChild(make("strong", "lc-result-metric__value", item.value));
        if (item.note) card.appendChild(make("small", "", item.note));
        metrics.appendChild(card);
      });
      target.appendChild(metrics);
    }

    if (output.formula_steps && output.formula_steps.length) {
      const details = document.createElement("details");
      details.className = "lc-formula";
      details.setAttribute("data-lc-result-section", "formula");
      const summaryNode = document.createElement("summary");
      summaryNode.textContent = "See how we calculated it";
      details.appendChild(summaryNode);
      const list = make("ol", "lc-formula__steps");
      output.formula_steps.forEach(function (step, index) {
        const item = make("li", "", step);
        item.style.setProperty("--lc-item-order", String(index));
        list.appendChild(item);
      });
      details.appendChild(list);
      details.appendChild(make("p", "lc-formula__version", "Algorithm version " + output.algorithm_version));
      details.addEventListener("toggle", function () {
        if (!details.open || details.getAttribute("data-lc-event-sent") === "true") return;
        details.setAttribute("data-lc-event-sent", "true");
        emitLifecycle(root, "result-section-opened", "formula");
      });
      target.appendChild(details);
    }

    if (output.sections && output.sections.length) {
      const sections = make("div", "lc-result-sections");
      output.sections.forEach(function (item, index) {
        const card = document.createElement(index < 2 ? "article" : "details");
        card.className = "lc-result-section";
        card.style.setProperty("--lc-item-order", String(index));
        if (index < 2) {
          card.appendChild(make("h3", "", item.title));
          card.appendChild(make("p", "", item.body));
        } else {
          const sectionKey = "detail-" + String(index + 1);
          card.setAttribute("data-lc-result-section", sectionKey);
          card.appendChild(make("summary", "", item.title));
          card.appendChild(make("p", "", item.body));
          card.addEventListener("toggle", function () {
            if (!card.open || card.getAttribute("data-lc-event-sent") === "true") return;
            card.setAttribute("data-lc-event-sent", "true");
            emitLifecycle(root, "result-section-opened", sectionKey);
          });
        }
        sections.appendChild(card);
      });
      target.appendChild(sections);
    }

    const shareText = resultShareText(output);
    const actions = make("div", "lc-result-actions");
    const copyButton = make("button", "", "Copy result");
    copyButton.type = "button";
    copyButton.addEventListener("click", function () {
      copyResultText(shareText, copyButton);
    });
    const shareButton = make("button", "", "Share result");
    shareButton.type = "button";
    shareButton.addEventListener("click", function () {
      if (typeof navigator.share === "function") {
        navigator.share({ title: output.label, text: shareText }).catch(function () {});
      } else {
        copyResultText(shareText, shareButton);
      }
    });
    actions.append(copyButton, shareButton);
    target.appendChild(actions);
    target.appendChild(make("p", "lc-result-disclaimer", output.disclaimer));
    void target.offsetWidth;
    target.classList.add("is-visible");
    if (offer) offer.hidden = false;
    const status = root.querySelector("[data-lc-status]");
    status.textContent = output.label + " calculated. Result " + output.primary + ".";
    requestAnimationFrame(function () { title.focus({ preventScroll: true }); target.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" }); });
  }

  function mount(root) {
    if (root.getAttribute("data-lc-mounted") === "true") return;
    root.setAttribute("data-lc-mounted", "true");
    const algorithmKey = root.getAttribute("data-algorithm");
    const calculatorKey = root.getAttribute("data-calculator-key");
    const form = root.querySelector("[data-lc-form]");
    const error = root.querySelector("[data-lc-error]");
    const target = root.querySelector("[data-lc-result]");
    if (!form || !ALGORITHMS[algorithmKey]) return;
    const offer = root.querySelector("[data-lc-offer]");
    if (offer) offer.hidden = true;
    mountSegmentedDates(form);

    if (calculatorKey === "ideal_partner") {
      const readingSelect = form.querySelector('[data-lc-field="reading_version"]');
      const readingChoices = Array.from(root.querySelectorAll("[data-lc-reading-choice]"));
      const applyReadingChoice = function (value) {
        if (!readingSelect) return;
        readingSelect.value = value;
        readingSelect.removeAttribute("aria-invalid");
        root.setAttribute("data-reading-choice", value);
        readingChoices.forEach(function (choice) {
          choice.setAttribute("aria-pressed", String(choice.getAttribute("data-lc-reading-choice") === value));
        });
        const panel = root.querySelector(".lc-love-form-panel");
        if (panel) panel.classList.add("is-ready");
      };
      readingChoices.forEach(function (choice) {
        choice.addEventListener("click", function () {
          applyReadingChoice(choice.getAttribute("data-lc-reading-choice"));
        });
      });
      if (readingSelect && readingSelect.value) applyReadingChoice(readingSelect.value);
    }

    emitLifecycle(root, "calculator-viewed", "page");

    const page = root.closest(".lc-page");
    if (page) {
      page.querySelectorAll("[data-lc-related-calculator]").forEach(function (link) {
        link.addEventListener("click", function () {
          emitLifecycle(root, "related-calculator-clicked", link.getAttribute("data-lc-related-calculator"));
        });
      });
    }
    if (offer) {
      offer.querySelectorAll("[data-lc-full-reading-cta]").forEach(function (link) {
        link.addEventListener("click", function () {
          emitLifecycle(root, "full-reading-cta-clicked", link.getAttribute("data-lc-full-reading-cta"));
        });
      });
      if (typeof window.IntersectionObserver === "function") {
        const offerObserver = new window.IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting || offer.getAttribute("data-lc-view-sent") === "true") return;
            offer.setAttribute("data-lc-view-sent", "true");
            emitLifecycle(root, "full-reading-cta-viewed", "result");
            offerObserver.unobserve(offer);
          });
        }, { threshold: 0.3 });
        offerObserver.observe(offer);
      }
    }
    let started = false;
    form.addEventListener("input", function () {
      error.textContent = "";
      error.hidden = true;
      form.querySelectorAll("[aria-invalid='true']").forEach(function (field) { field.removeAttribute("aria-invalid"); });
      if (!started) {
        started = true;
        emitLifecycle(root, "calculator-started", "first-input");
      }
    });
    form.addEventListener("submit", function (event) {
      event.preventDefault();
      error.textContent = "";
      error.hidden = true;
      target.hidden = true;
      if (offer) offer.hidden = true;
      try {
        const output = ALGORITHMS[algorithmKey](collect(form));
        output.calculator_key = calculatorKey;
        renderResult(root, output);
        if (offer && typeof window.IntersectionObserver !== "function"
            && offer.getAttribute("data-lc-view-sent") !== "true") {
          offer.setAttribute("data-lc-view-sent", "true");
          emitLifecycle(root, "full-reading-cta-viewed", "result");
        }
        emitLifecycle(root, "calculator-completed", "result");
      } catch (exception) {
        if (offer) offer.hidden = true;
        const message = exception && exception.message ? exception.message : "Check the information and try again.";
        error.textContent = message;
        error.hidden = false;
        markInvalidField(form, message);
        error.focus();
        emitLifecycle(root, "calculation-error", "validation");
      }
    });
  }

  function mountScrollReveals(page) {
    if (!page || page.getAttribute("data-lc-motion-mounted") === "true") return;
    page.setAttribute("data-lc-motion-mounted", "true");
    const items = Array.from(page.querySelectorAll("[data-lc-reveal]"));
    if (!items.length) return;
    const reducedMotion = typeof window.matchMedia === "function"
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion || typeof window.IntersectionObserver !== "function") {
      items.forEach(function (item) { item.classList.add("is-seen"); });
      return;
    }
    items.forEach(function (item, index) {
      item.style.setProperty("--lc-reveal-order", String(index % 4));
    });
    page.classList.add("lc-motion-ready");
    const observer = new window.IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-seen");
        observer.unobserve(entry.target);
      });
    }, {
      root: null,
      rootMargin: "0px 0px -8% 0px",
      threshold: 0.08
    });
    items.forEach(function (item) { observer.observe(item); });
  }

  function mountGeneratedIcons(scope) {
    (scope || document).querySelectorAll("[data-lc-theme-generated-icon]").forEach(function (image) {
      if (image.getAttribute("data-lc-icon-mounted") === "true") return;
      const section = image.closest(".luvire-calculator-page-section");
      const target = section && section.querySelector(".lc-hero__sigil");
      if (!target) return;
      image.setAttribute("data-lc-icon-mounted", "true");
      image.classList.add("lc-hero__generated-icon");
      image.hidden = false;
      target.textContent = "";
      target.appendChild(image);
    });
    (scope || document).querySelectorAll("[data-lc-theme-choice-art]").forEach(function (image) {
      const section = image.closest(".luvire-calculator-page-section");
      const kind = image.getAttribute("data-lc-theme-choice-art");
      const target = section && section.querySelector('[data-lc-choice-image="' + kind + '"]');
      if (!target) return;
      target.src = image.src;
      image.remove();
    });
  }

  function mountAll(scope) {
    const target = scope || document;
    mountGeneratedIcons(target);
    target.querySelectorAll(".lc-page").forEach(mountScrollReveals);
    target.querySelectorAll(".lc-app").forEach(mount);
  }

  function mountHub(root) {
    if (root.getAttribute("data-lc-hub-mounted") === "true") return;
    root.setAttribute("data-lc-hub-mounted", "true");
    const buttons = Array.from(root.querySelectorAll("[data-lc-filter]"));
    const cards = Array.from(root.querySelectorAll("[data-lc-category]"));
    const sections = Array.from(root.querySelectorAll("[data-lc-category-section]"));
    const status = root.querySelector("[data-lc-hub-status]");
    const search = root.querySelector("[data-lc-search]");
    let selected = "all";

    function applyHubFilters() {
      const query = search ? search.value.trim().toLocaleLowerCase() : "";
      let visible = 0;
      cards.forEach(function (card) {
        const categoryMatch = selected === "all" || card.getAttribute("data-lc-category") === selected;
        const searchText = card.getAttribute("data-lc-search-text") || "";
        const queryMatch = !query || searchText.includes(query);
        const match = categoryMatch && queryMatch;
        card.hidden = !match;
        if (match) visible += 1;
      });
      sections.forEach(function (item) {
        const hasVisibleCard = Array.from(item.querySelectorAll("[data-lc-category]")).some(function (card) {
          return !card.hidden;
        });
        item.hidden = !hasVisibleCard;
      });
      if (status) {
        status.textContent = visible
          ? "Showing " + visible + (visible === 1 ? " calculator." : " calculators.")
          : "No calculators match that search yet.";
      }
    }

    buttons.forEach(function (button) {
      button.addEventListener("click", function () {
        selected = button.getAttribute("data-lc-filter");
        buttons.forEach(function (item) {
          item.setAttribute("aria-pressed", String(item === button));
        });
        applyHubFilters();
      });
    });
    if (search) {
      search.addEventListener("input", applyHubFilters);
    }
  }

  function mountHubs(scope) {
    (scope || document).querySelectorAll("[data-lc-hub]").forEach(mountHub);
  }

  window.LuvireCalculator = {
    version: VERSION,
    algorithms: ALGORITHMS,
    calculate: function (algorithm, input) {
      if (!ALGORITHMS[algorithm]) throw new Error("Unknown algorithm: " + algorithm);
      return ALGORITHMS[algorithm](input || {});
    },
    mount: mount,
    mountAll: mountAll,
    mountHub: mountHub,
    mountHubs: mountHubs,
    helpers: {
      reduceNumber: reduceNumber,
      normalizeName: normalizeName,
      nameNumber: nameNumber,
      lifePath: lifePath,
      parseDate: parseDate,
      formatSegmentedDateParts: formatSegmentedDateParts,
      dateFieldKeyFromError: dateFieldKeyFromError
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      mountAll(document);
      mountHubs(document);
    });
  } else {
    mountAll(document);
    mountHubs(document);
  }
}());
